import React, {useEffect, useMemo, useState} from "react"
import {ThemeProvider} from "@mui/material";
import {useUser} from "@/context/UserProvider";
import {createTheme} from "@mui/material/styles";
import {APIGet} from "@api";
import {VISIBLE_TOP_VAR, VISIBLE_HEIGHT_VAR} from "@/utils/useVisibleViewportVar";
import {packagingTokens} from "@/theme/packagingTokens";

const {REACT_APP_API_URL} = process.env

const colors = {
    primary: "#158F48",
    secondary: "#1E5BA8",
}

const stylesInit = {
    primaryColor: colors.primary,
    secondaryColor: colors.secondary,
}

// Height (px) reserved at the top of the visible band so dialogs clear the
// parent dashboard's fixed header that overlays the top of the iframe.
const DASHBOARD_HEADER_GAP = 80

export default function PackagingUiProvider(props: any) {

    const {user, token} = useUser()
    const [primaryColor, setPrimaryColor] = useState(stylesInit.primaryColor);
    const [secondaryColor, setSecondaryColor] = useState(stylesInit.secondaryColor);

    const theme = useMemo(() => createTheme({
        palette: {
            primary: {
                main: primaryColor,
                contrastText: "#ffffff",
            },
            secondary: {
                main: secondaryColor,
                contrastText: "#ffffff",
            },
            text: {
                secondary: "#525252",
            }
        },
        typography: {
            // Rémi: Inter is the UI face (packaging-v3). Roboto stays as fallback.
            fontFamily: packagingTokens.font.family,
            h1: {
                fontFamily: 'Roboto',
                fontSize: 48,
                lineHeight: "58px",
                letterSpacing: "0.00938em",
                fontWeight: 900,
            },
            h2: {
                fontSize: 24,
                lineHeight: '34px',
                fontWeight: 400,
            },
            h5: {
                fontWeight: '500',
            }
        },
        components: {
            // The app is embedded as a content-sized iframe, so `position: fixed`
            // modals would otherwise center within the full iframe height and open
            // off-screen. Anchor every dialog near the top of the user's visible
            // band and cap its height to that band (variables published by
            // useVisibleViewportVar). The DASHBOARD_HEADER_GAP clears the parent
            // dashboard's fixed header, which paints over the top of the iframe and
            // is therefore still counted as "visible" by the IntersectionObserver.
            // Fallbacks keep dialogs near the top when the variables are absent
            // (e.g. outside the iframe / in tests).
            MuiDialog: {
                styleOverrides: {
                    container: {
                        alignItems: "flex-start",
                    },
                    paper: {
                        marginTop: `calc(var(${VISIBLE_TOP_VAR}, 0px) + ${DASHBOARD_HEADER_GAP}px)`,
                        // max() floor: if the visible-height variable is ever published
                        // from a degenerate layout (collapsed body), the calc() would go
                        // negative and clamp the paper to 0px — an invisible dialog over
                        // a full backdrop. Never let a paper shrink below a usable size.
                        maxHeight: `max(300px, calc(var(${VISIBLE_HEIGHT_VAR}, 100%) - ${DASHBOARD_HEADER_GAP + 32}px))`,
                        borderRadius: packagingTokens.radius.control,
                    },
                    // Full-screen dialogs stay square regardless of the surface radius.
                    paperFullScreen: {
                        borderRadius: 0,
                    },
                },
            },
            // Rémi control radius — the dashboard's single "REMI_RADIUS" knob
            // (8px). Inputs/selects/autocompletes and menus/popovers read it here
            // so packaging form controls match the dashboard (claude-design.md
            // §3.8) without per-component sx overrides.
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: packagingTokens.radius.control,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    rounded: {
                        borderRadius: packagingTokens.radius.control,
                    },
                },
            },
        }
    }), [primaryColor, secondaryColor]);

    useEffect(() => {
        if (!!user) fetchStylesData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const fetchStylesData = () => {
        APIGet(REACT_APP_API_URL + "/user/setting", token).then((res: any) => {
            if (!!res.parsedBody && !!res.parsedBody.dash_design) {
                let _styles = res.parsedBody.dash_design
                setPrimaryColor(_styles.primaryColor)
                setSecondaryColor(_styles.secondaryColor)
            }
        }).catch(() => {
            // handleError(t("settings:error_fetching_styles"))
        }).finally(() => {
            // setLoading(false)
            // setStylesDidLoad(true)
        })
    }

    return (
        <ThemeProvider theme={theme}>
            {props.children}
        </ThemeProvider>
    )
}